import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'

interface HiveAmountPreviewProps {
  apiaryName: string
  apiaryColor: string
  hiveAmount: number
  hivePrefix: string
  hiveOffset: number
}

const HiveAmountPreview: React.FC<HiveAmountPreviewProps> = ({
  apiaryName,
  apiaryColor,
  hiveAmount,
  hivePrefix,
  hiveOffset,
}) => {
  const theme = useTheme()

  // Generate array of hives to display
  const hives = Array.from({ length: Math.min(hiveAmount, 50) }, (_, index) => ({
    name: `${hivePrefix}${hiveOffset + index}`,
  }))

  if (hiveAmount === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          color: 'text.secondary',
          backgroundColor: theme.palette.grey[50],
          borderRadius: 1,
          border: `1px dashed ${theme.palette.grey[300]}`,
        }}
      >
        <Typography variant="body2">
          Set hive amount above 0 to see preview
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="overline" display="block" gutterBottom color="text.secondary">
        Location preview: {apiaryName}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1.5,
          overflowX: 'auto',
          py: 2,
          px: 1,
          backgroundColor: theme.palette.grey[50],
          borderRadius: 1,
          minHeight: 100,
        }}
      >
        {hives.map((hive, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 'fit-content',
            }}
          >
            {/* Simplified Hive Icon */}
            <Box
              sx={{
                width: 48,
                height: 60,
                backgroundColor: apiaryColor,
                borderRadius: '4px 4px 0 0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                border: '2px solid',
                borderColor: 'divider',
                borderBottom: '3px solid #666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                },
              }}
            >
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
                #{hiveOffset + index}
              </Typography>
            </Box>

            {/* Hive Label */}
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                fontSize: '0.7rem',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                fontWeight: 500,
              }}
            >
              {hive.name}
            </Typography>
          </Box>
        ))}
      </Box>

      {hiveAmount > 50 && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
          Preview limited to 50 hives (showing first 50 of {hiveAmount})
        </Typography>
      )}
    </Box>
  )
}

export default HiveAmountPreview
